import {
  normalizeForCampaignSearch,
  parseCampaignSearchTokens,
} from '@/utils/campaignSearch';

export type AutomationCampaignOption = {
  campaignId: string;
  entityRef?: string;
  name: string | null;
  adProduct: string;
  state?: string | null;
  parentLabel?: string | null;
  portfolioId?: string | null;
};

export const AD_GROUP_ENTITY_PREFIX = 'ad_group:';
export const PORTFOLIO_ENTITY_PREFIX = 'portfolio:';

export type CampaignAdGroupScopeKind = 'all' | 'campaigns' | 'ad_groups' | 'portfolios';

export function usesCampaignAdGroupScope(entityType: string): boolean {
  return entityType === 'search_term' || entityType === 'st_harvest' || entityType === 'target';
}

export function usesPortfolioCampaignScope(entityType: string): boolean {
  return entityType === 'campaign' || entityType === 'placement' || usesCampaignAdGroupScope(entityType);
}

export function stripAdGroupPrefix(ref: string): string {
  return ref.startsWith(AD_GROUP_ENTITY_PREFIX) ? ref.slice(AD_GROUP_ENTITY_PREFIX.length) : ref;
}

export function stripPortfolioPrefix(ref: string): string {
  return ref.startsWith(PORTFOLIO_ENTITY_PREFIX) ? ref.slice(PORTFOLIO_ENTITY_PREFIX.length) : ref;
}

export function stripEntityPrefix(ref: string): string {
  return stripPortfolioPrefix(stripAdGroupPrefix(ref));
}

export function withAdGroupPrefix(ref: string): string {
  const bare = stripEntityPrefix(ref);
  return bare ? `${AD_GROUP_ENTITY_PREFIX}${bare}` : bare;
}

export function withPortfolioPrefix(ref: string): string {
  const bare = stripEntityPrefix(ref);
  return bare ? `${PORTFOLIO_ENTITY_PREFIX}${bare}` : bare;
}

export function inferScopeKind(
  entityType: string,
  entityScope: string | undefined,
  entities?: Array<{ entityRef: string }>,
  ruleOptions?: Record<string, unknown> | null
): CampaignAdGroupScopeKind {
  if (entityScope !== 'specific') return 'all';
  const kindRaw = String(ruleOptions?.scope_kind ?? ruleOptions?.scopeKind ?? '').toLowerCase();
  if (kindRaw === 'portfolio' || kindRaw === 'portfolios') return 'portfolios';
  if ((entities ?? []).some((e) => e.entityRef.startsWith(PORTFOLIO_ENTITY_PREFIX))) {
    return 'portfolios';
  }
  if (!usesCampaignAdGroupScope(entityType)) return 'campaigns';
  if (kindRaw === 'ad_group' || kindRaw === 'ad_groups') return 'ad_groups';
  if (kindRaw === 'campaign' || kindRaw === 'campaigns') return 'campaigns';
  if ((entities ?? []).some((e) => e.entityRef.startsWith(AD_GROUP_ENTITY_PREFIX))) {
    return 'ad_groups';
  }
  return 'campaigns';
}

export function scopeHintNoun(
  entityType: string,
  entityScope: string | undefined,
  entities?: Array<{ entityRef: string }>,
  ruleOptions?: Record<string, unknown> | null
): string | null {
  if (!usesPortfolioCampaignScope(entityType)) return null;
  const kind = inferScopeKind(entityType, entityScope, entities, ruleOptions);
  if (kind === 'portfolios') return 'portfolios';
  if (!usesCampaignAdGroupScope(entityType)) return null;
  return kind === 'ad_groups' ? 'ad groups' : 'campaigns';
}

export function listEntityTypeForScope(entityType: string, kind: CampaignAdGroupScopeKind): string {
  if (kind === 'portfolios') return 'portfolio';
  if (!usesCampaignAdGroupScope(entityType) || kind === 'all') return entityType;
  return kind === 'ad_groups' ? 'ad_group' : 'campaign';
}

export function pruneSelectedByAdTypes(
  selected: AutomationCampaignOption[],
  adTypes: string[],
  kind?: CampaignAdGroupScopeKind
): AutomationCampaignOption[] {
  if (kind === 'portfolios') return selected;
  const allowed = new Set(adTypes.map((t) => t.toUpperCase()));
  return selected.filter((c) => allowed.has(c.adProduct.toUpperCase()));
}

export function filterAutomationCampaigns(
  options: AutomationCampaignOption[],
  raw: string,
  limit = 200
): AutomationCampaignOption[] {
  const tokens = parseCampaignSearchTokens(raw);
  const matched =
    tokens.length === 0
      ? options
      : options.filter((c) => {
          const haystack = normalizeForCampaignSearch(
            `${c.name || ''} ${c.campaignId} ${c.adProduct} ${c.parentLabel || ''}`
          );
          return tokens.every((t) => haystack.includes(t));
        });
  return matched.slice(0, limit);
}

export function toggleAutomationCampaign(
  selected: AutomationCampaignOption[],
  option: AutomationCampaignOption,
  checked: boolean
): AutomationCampaignOption[] {
  if (checked) {
    if (selected.some((c) => c.campaignId === option.campaignId)) return selected;
    return [...selected, option];
  }
  return selected.filter((c) => c.campaignId !== option.campaignId);
}

export function mergeSelectedNames(
  selected: AutomationCampaignOption[],
  options: AutomationCampaignOption[]
): AutomationCampaignOption[] {
  if (options.length === 0) return selected;
  const byId = new Map(options.map((o) => [o.campaignId, o]));
  return selected.map((s) => {
    const match = byId.get(s.campaignId);
    return match ? { ...s, ...match, name: match.name ?? s.name } : s;
  });
}

export function selectedFromRuleEntities(
  entities: Array<{ entityRef: string; adProduct: string }> | undefined
): AutomationCampaignOption[] {
  return (entities ?? []).map((e) => {
    const id = stripEntityPrefix(e.entityRef);
    return {
      campaignId: id,
      entityRef: id,
      name: null,
      adProduct: e.adProduct,
    };
  });
}

export function entitiesPayload(selected: AutomationCampaignOption[]) {
  return selected.map((c) => ({
    entityRef: c.entityRef || c.campaignId,
    adProduct: c.adProduct,
  }));
}

export function entitiesPayloadForScope(
  selected: AutomationCampaignOption[],
  entityType: string,
  kind: CampaignAdGroupScopeKind
) {
  const prefixAdGroups = usesCampaignAdGroupScope(entityType) && kind === 'ad_groups';
  const prefixPortfolios = kind === 'portfolios';
  return selected.map((c) => {
    const bare = stripEntityPrefix(c.entityRef || c.campaignId);
    return {
      entityRef: prefixAdGroups
        ? withAdGroupPrefix(bare)
        : prefixPortfolios
          ? withPortfolioPrefix(bare)
          : bare,
      adProduct: c.adProduct || 'SP',
    };
  });
}

export function mapEntityApiItems(
  items: Array<{
    entityRef?: string;
    campaignId: string;
    name: string | null;
    adProduct: string;
    state?: string | null;
    parentLabel?: string | null;
    portfolioId?: string | null;
  }>
): AutomationCampaignOption[] {
  return items.map((item) => {
    const ref = item.entityRef || item.campaignId;
    return {
      campaignId: ref,
      entityRef: ref,
      name: item.name,
      adProduct: item.adProduct,
      state: item.state,
      parentLabel: item.parentLabel,
      portfolioId: item.portfolioId ?? null,
    };
  });
}

export function ruleScopeHint(entityScope: string, count: number, noun = 'campaigns'): string {
  if (entityScope === 'specific') {
    const n = noun;
    const singular = n.endsWith('s') ? n.slice(0, -1) : n;
    return `${count} ${count !== 1 ? n : singular}`;
  }
  return `All ${noun}`;
}
